/**
 * Stage 73.20o — Employee Assets & Documents page.
 *
 * Two-section page accessible via Employee profile or directly at
 * /employees/:id/assets-docs. Captures:
 *   - Assets issued to the employee (laptop, mobile, SIM, etc.)
 *   - Documents attached to the employee profile (offer letter,
 *     CTC acceptance, handover forms, etc.)
 *
 * Both surfaces are read by the F&F compute endpoint — pending assets
 * block F&F finalisation; lost/damaged assets auto-add a recovery line
 * to the F&F deductions checklist.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Laptop, Smartphone, FileText, Upload, Download, Plus, Trash2, ArrowLeft,
  Check, Loader2, AlertTriangle, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  issued: 'bg-amber-100 text-amber-800 border-amber-200',
  returned: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  lost: 'bg-rose-100 text-rose-800 border-rose-200',
  damaged: 'bg-orange-100 text-orange-800 border-orange-200',
  written_off: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function EmployeeAssetsDocs() {
  const { employee_id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [assets, setAssets] = useState([]);
  const [documents, setDocuments] = useState([]);
  // Stage 73.20di — masters replace the old hardcoded meta endpoints.
  const [assetTypes, setAssetTypes] = useState([]);         // active rows from asset_types master
  const [docTypes, setDocTypes] = useState([]);             // active rows from document_types master
  const [assetMeta, setAssetMeta] = useState({ categories: [], statuses: [], terminal_statuses: [] });
  const [docMeta, setDocMeta] = useState({ categories: [] });
  const [clearance, setClearance] = useState(null);
  const [loading, setLoading] = useState(true);

  // Asset form (multi-row — Stage 73.20do)
  const [showAssetForm, setShowAssetForm] = useState(false);
  const _newAssetRow = () => ({
    asset_type: '', asset_name: '', serial_number: '',
    handover_date: new Date().toISOString().slice(0, 10),
    expected_return_date: '', condition_at_issue: '', remarks: '', notes: '',
  });
  const [assetRows, setAssetRows] = useState([_newAssetRow()]);
  const [assetSaving, setAssetSaving] = useState(false);

  // Per-asset documents dialog (Stage 73.20do)
  const [assetDocsFor, setAssetDocsFor] = useState(null);  // holds the asset row
  const [assetDocs, setAssetDocs] = useState([]);          // list of docs linked to it
  const [assetDocsLoading, setAssetDocsLoading] = useState(false);
  const [assetDocForm, setAssetDocForm] = useState({
    category: '', custom_label: '', file: null, notes: '',
  });
  const [assetDocUploading, setAssetDocUploading] = useState(false);

  // Document form
  const [showDocForm, setShowDocForm] = useState(false);
  const [docForm, setDocForm] = useState({
    category: '', custom_label: '', notes: '', related_asset_id: '',
    expiry_date: '', file: null,
  });
  const [docUploading, setDocUploading] = useState(false);

  // Asset update modal
  const [updatingAsset, setUpdatingAsset] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    status: '', returned_date: '', recovery_amount: '', condition_at_return: '', remarks: '', notes: '',
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [emp, a, d, am, dm, c] = await Promise.all([
        axios.get(`${API}/employees/${employee_id}`, { withCredentials: true }),
        axios.get(`${API}/employees/${employee_id}/assets`, { withCredentials: true }),
        axios.get(`${API}/employees/${employee_id}/documents`, { withCredentials: true }),
        axios.get(`${API}/employees/_meta/asset-categories`, { withCredentials: true }),
        axios.get(`${API}/employees/_meta/document-categories`, { withCredentials: true }),
        axios.get(`${API}/employees/${employee_id}/fnf-clearance-status`, { withCredentials: true }),
      ]);
      setEmployee(emp.data);
      setAssets(a.data || []);
      setDocuments(d.data || []);
      setAssetMeta(am.data);
      setDocMeta(dm.data);
      setClearance(c.data);
      // Stage 73.20di — replace hardcoded categories with master-driven lists.
      const companyId = emp.data?.employment_details?.company_id || '';
      if (companyId) {
        try {
          const [ats, dts] = await Promise.all([
            axios.get(`${API}/masters/asset-types?company_id=${companyId}&active_only=true`, { withCredentials: true }),
            axios.get(`${API}/masters/document-types?company_id=${companyId}`, { withCredentials: true }),
          ]);
          setAssetTypes(ats.data || []);
          setDocTypes((dts.data || []).filter((x) => x.active !== false));
        } catch { /* ignore — masters may not be seeded yet */ }
      }
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [employee_id]);

  useEffect(() => { loadAll(); }, [employee_id, loadAll]);


  // Stage 73.20do — Multi-row asset issue. Every row is sent to the
  // bulk endpoint which validates and rolls back atomically.
  const updateAssetRow = (idx, field, value) => setAssetRows(rows => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [field]: value };
    return copy;
  });
  const addAssetRow = () => setAssetRows(rows => [...rows, _newAssetRow()]);
  const removeAssetRow = (idx) => setAssetRows(rows => rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx));

  const submitAsset = async () => {
    // Client-side validation (server also enforces).
    for (let i = 0; i < assetRows.length; i += 1) {
      const r = assetRows[i];
      if (!r.asset_type) { toast.error(`Row ${i + 1}: Pick an Asset Type`); return; }
      if (!r.asset_name.trim()) { toast.error(`Row ${i + 1}: Asset name is required`); return; }
      const chosen = assetTypes.find(t => t.name === r.asset_type);
      if (chosen?.serial_number_required && !r.serial_number.trim()) {
        toast.error(`Row ${i + 1}: Serial number is required for ${r.asset_type}`);
        return;
      }
    }
    setAssetSaving(true);
    try {
      if (assetRows.length === 1) {
        // Preserve backward compat — single-row uses the classic endpoint.
        await axios.post(`${API}/employees/${employee_id}/assets`, assetRows[0], { withCredentials: true });
        toast.success('Asset issued');
      } else {
        const res = await axios.post(
          `${API}/employees/${employee_id}/assets/bulk`,
          { assets: assetRows },
          { withCredentials: true },
        );
        toast.success(`${res.data.issued} asset${res.data.issued === 1 ? '' : 's'} issued`);
      }
      setShowAssetForm(false);
      setAssetRows([_newAssetRow()]);
      await loadAll();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setAssetSaving(false);
  };

  // Stage 73.20do — per-asset docs dialog helpers.
  const openAssetDocs = async (asset) => {
    setAssetDocsFor(asset);
    setAssetDocsLoading(true);
    setAssetDocForm({ category: '', custom_label: '', file: null, notes: '' });
    try {
      const r = await axios.get(
        `${API}/employees/${employee_id}/assets/${asset.id}/documents`,
        { withCredentials: true },
      );
      setAssetDocs(r.data || []);
    } catch (e) {
      toast.error('Failed to load asset documents: ' + (e?.response?.data?.detail || e.message));
      setAssetDocs([]);
    }
    setAssetDocsLoading(false);
  };

  const uploadAssetDoc = async () => {
    if (!assetDocsFor) return;
    if (!assetDocForm.category) { toast.error('Pick a Document Type'); return; }
    if (!assetDocForm.file) { toast.error('Pick a file to upload'); return; }
    setAssetDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', assetDocForm.file);
      fd.append('category', assetDocForm.category);
      if (assetDocForm.custom_label) fd.append('custom_label', assetDocForm.custom_label);
      if (assetDocForm.notes) fd.append('notes', assetDocForm.notes);
      fd.append('related_asset_id', assetDocsFor.id);
      await axios.post(`${API}/employees/${employee_id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document attached to asset');
      setAssetDocForm({ category: '', custom_label: '', file: null, notes: '' });
      // Re-load per-asset docs + main asset list (for the badge count).
      await openAssetDocs(assetDocsFor);
      await loadAll();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
    setAssetDocUploading(false);
  };

  const submitAssetUpdate = async () => {
    try {
      const payload = {};
      if (updateForm.status) payload.status = updateForm.status;
      if (updateForm.returned_date) payload.returned_date = updateForm.returned_date;
      if (updateForm.recovery_amount) payload.recovery_amount = parseFloat(updateForm.recovery_amount);
      if (updateForm.notes) payload.notes = updateForm.notes;
      await axios.patch(
        `${API}/employees/${employee_id}/assets/${updatingAsset.id}`,
        payload,
        { withCredentials: true },
      );
      toast.success('Asset updated');
      setUpdatingAsset(null);
      await loadAll();
    } catch (e) {
      toast.error('Update failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteAsset = async (asset_id) => {
    if (!window.confirm('Delete this asset entry? (Soft delete — kept in audit log)')) return;
    try {
      await axios.delete(`${API}/employees/${employee_id}/assets/${asset_id}`, { withCredentials: true });
      toast.success('Asset removed');
      await loadAll();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const uploadDocument = async () => {
    if (!docForm.category) { toast.error('Pick a Document Type'); return; }
    if (!docForm.file) { toast.error('Pick a file to upload'); return; }
    // Enforce related_asset_id when the chosen type is asset-related.
    const chosenType = docTypes.find((t) => t.name === docForm.category);
    if (chosenType?.is_asset_related && !docForm.related_asset_id) {
      toast.error('This document type requires a Related Asset — please select one.');
      return;
    }
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', docForm.file);
      fd.append('category', docForm.category);
      if (docForm.custom_label) fd.append('custom_label', docForm.custom_label);
      if (docForm.notes) fd.append('notes', docForm.notes);
      if (docForm.related_asset_id) fd.append('related_asset_id', docForm.related_asset_id);
      if (docForm.expiry_date) fd.append('expiry_date', docForm.expiry_date);
      await axios.post(`${API}/employees/${employee_id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setShowDocForm(false);
      setDocForm({ category: '', custom_label: '', notes: '', related_asset_id: '', expiry_date: '', file: null });
      await loadAll();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDocUploading(false);
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/employees/${employee_id}/documents/${doc.id}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Download failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const previewDocument = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/employees/${employee_id}/documents/${doc.id}/download?preview=true`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type || res.headers['content-type'] }));
      window.open(blobUrl, '_blank');
    } catch (e) {
      toast.error('Preview failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteDocument = async (doc_id) => {
    if (!window.confirm('Remove this document? (Soft delete — file kept in storage)')) return;
    try {
      await axios.delete(`${API}/employees/${employee_id}/documents/${doc_id}`, { withCredentials: true });
      toast.success('Document removed');
      await loadAll();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return <MainLayout><div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></MainLayout>;
  }

  const empName = `${employee?.personal_details?.first_name || ''} ${employee?.personal_details?.last_name || ''}`.trim() || '—';
  const empCode = employee?.employment_details?.employee_code || '';

  return (
    <MainLayout>
      <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="employee-assets-docs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/employees" className="text-xs text-blue-600 hover:underline">
              <ArrowLeft className="w-3 h-3 inline" /> Back to Employees
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">
              Assets &amp; Documents
            </h1>
            <p className="text-sm text-slate-600">
              {empName} <span className="text-slate-400">·</span> {empCode}
            </p>
          </div>
        </div>

        {/* F&F clearance gate */}
        {clearance && (
          <Card className={`p-4 ${clearance.cleared ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="fnf-clearance-card">
            <div className="flex items-start gap-3">
              {clearance.cleared ? (
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`font-semibold ${clearance.cleared ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {clearance.cleared
                    ? '✓ F&F Clearance: All assets accounted for'
                    : `⚠ F&F Clearance: ${clearance.pending_count} asset(s) still pending return`
                  }
                </p>
                {!clearance.cleared && (
                  <p className="text-xs text-amber-700 mt-0.5">
                    F&F finalisation is blocked until every issued asset is marked Returned / Lost / Damaged / Written-off.
                  </p>
                )}
                {clearance.total_recovery > 0 && (
                  <p className="text-xs text-rose-700 mt-1">
                    💰 Recovery for Lost/Damaged: <b>₹{clearance.total_recovery.toLocaleString('en-IN')}</b> — auto-added to F&F deductions.
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* === ASSETS section === */}
        <Card className="p-4" data-testid="assets-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Laptop className="w-5 h-5" /> Issued Assets ({assets.length})
            </h2>
            <Button
              onClick={() => setShowAssetForm(s => !s)}
              size="sm"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
              data-testid="add-asset-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Issue New Asset
            </Button>
          </div>

          {showAssetForm && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3" data-testid="asset-form">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-600">
                  Issuing <b>{assetRows.length}</b> asset{assetRows.length === 1 ? '' : 's'}. Add more rows to hand over multiple assets in one go.
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={addAssetRow}
                  data-testid="add-asset-row-btn"
                  className="text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add another asset
                </Button>
              </div>
              {assetRows.map((row, idx) => (
                <div
                  key={idx}
                  className="border border-blue-100 rounded p-2 mb-2 bg-white relative"
                  data-testid={`asset-form-row-${idx}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-blue-700">Asset #{idx + 1}</span>
                    {assetRows.length > 1 && (
                      <button
                        onClick={() => removeAssetRow(idx)}
                        className="text-rose-600 hover:text-rose-700"
                        title="Remove this row"
                        data-testid={`remove-asset-row-${idx}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <label>
                      <span className="text-slate-700 font-medium">Asset Type *</span>
                      <select
                        value={row.asset_type}
                        onChange={e => updateAssetRow(idx, 'asset_type', e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-type-select-${idx}`}
                      >
                        <option value="">— select —</option>
                        {assetTypes.map(t => (
                          <option key={t.id} value={t.name}>{`${t.name}${t.category ? ` · ${t.category}` : ''}`}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="text-slate-700 font-medium">Asset Name / Model *</span>
                      <input
                        value={row.asset_name}
                        onChange={e => updateAssetRow(idx, 'asset_name', e.target.value)}
                        placeholder="e.g. Dell Latitude 5420, iPhone 13"
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-name-input-${idx}`}
                      />
                    </label>
                    <label>
                      <span className="text-slate-700 font-medium">Serial Number / IMEI</span>
                      <input
                        value={row.serial_number}
                        onChange={e => updateAssetRow(idx, 'serial_number', e.target.value)}
                        placeholder="Optional (required for some types)"
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-serial-input-${idx}`}
                      />
                    </label>
                    <label>
                      <span className="text-slate-700 font-medium">Handover Date *</span>
                      <input
                        type="date"
                        value={row.handover_date}
                        onChange={e => updateAssetRow(idx, 'handover_date', e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-handover-date-${idx}`}
                      />
                    </label>
                    <label>
                      <span className="text-slate-700 font-medium">Expected Return Date</span>
                      <input
                        type="date"
                        value={row.expected_return_date}
                        onChange={e => updateAssetRow(idx, 'expected_return_date', e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-expected-return-date-${idx}`}
                      />
                    </label>
                    <label>
                      <span className="text-slate-700 font-medium">Condition at Issue</span>
                      <input
                        value={row.condition_at_issue}
                        onChange={e => updateAssetRow(idx, 'condition_at_issue', e.target.value)}
                        placeholder="e.g. New, Good, Refurbished"
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-condition-at-issue-${idx}`}
                      />
                    </label>
                    <label className="md:col-span-2">
                      <span className="text-slate-700 font-medium">Remarks</span>
                      <textarea
                        value={row.remarks}
                        onChange={e => updateAssetRow(idx, 'remarks', e.target.value)}
                        rows={2}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid={`asset-remarks-${idx}`}
                      />
                    </label>
                  </div>
                </div>
              ))}
              {assetTypes.length === 0 && (
                <p className="text-[11px] text-amber-700 mb-2">
                  No active asset types configured. Please add them under Masters → Asset Types first.
                </p>
              )}
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => { setShowAssetForm(false); setAssetRows([_newAssetRow()]); }}>Cancel</Button>
                <Button size="sm" onClick={submitAsset} disabled={assetSaving} data-testid="asset-submit-btn">
                  {assetSaving
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : assetRows.length === 1 ? 'Issue Asset' : `Issue ${assetRows.length} Assets`}
                </Button>
              </div>
            </div>
          )}

          {assets.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No assets issued yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="assets-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Serial</th>
                    <th className="text-left p-2">Handover</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Returned</th>
                    <th className="text-right p-2">Recovery</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map(a => (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`asset-row-${a.id}`}>
                      <td className="p-2">{a.asset_type}</td>
                      <td className="p-2 font-medium">{a.asset_name}</td>
                      <td className="p-2 text-slate-600">{a.serial_number || '—'}</td>
                      <td className="p-2 text-slate-600">{a.handover_date}</td>
                      <td className="p-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] border ${STATUS_COLORS[a.status] || 'bg-slate-100'}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="p-2 text-slate-600">{a.returned_date || '—'}</td>
                      <td className="p-2 text-right text-rose-700 font-medium">
                        {a.recovery_amount ? `₹${Number(a.recovery_amount).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="p-2 text-right space-x-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openAssetDocs(a)}
                          data-testid={`asset-docs-btn-${a.id}`}
                          className="text-xs relative"
                          title="Attach or view documents for this asset"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Docs
                          {(a.document_count || 0) > 0 && (
                            <span
                              data-testid={`asset-doc-badge-${a.id}`}
                              className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold"
                            >
                              {a.document_count}
                            </span>
                          )}
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => { setUpdatingAsset(a); setUpdateForm({ status: a.status, returned_date: a.returned_date || '', recovery_amount: a.recovery_amount || '', notes: a.notes || '' }); }}
                          data-testid={`asset-update-btn-${a.id}`}
                        >
                          Update
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteAsset(a.id)} className="text-rose-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Asset Update modal */}
        {updatingAsset && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setUpdatingAsset(null)}>
            <div className="bg-white rounded-lg p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="asset-update-modal">              <h3 className="font-semibold text-slate-900 mb-3">
                Update Asset: {updatingAsset.asset_name}
              </h3>
              <div className="space-y-3 text-xs">
                <label className="block">
                  <span className="text-slate-700 font-medium">Status</span>
                  <select value={updateForm.status} onChange={e => setUpdateForm({ ...updateForm, status: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" data-testid="update-status-select">
                    {assetMeta.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-slate-700 font-medium">Returned Date (auto-filled for terminal statuses)</span>
                  <input type="date" value={updateForm.returned_date} onChange={e => setUpdateForm({ ...updateForm, returned_date: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                </label>
                {(updateForm.status === 'lost' || updateForm.status === 'damaged') && (
                  <label className="block">
                    <span className="text-slate-700 font-medium">Recovery Amount (₹)</span>
                    <input type="number" value={updateForm.recovery_amount} onChange={e => setUpdateForm({ ...updateForm, recovery_amount: e.target.value })}
                      placeholder="Deducted from F&F" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                    <p className="text-[10px] text-slate-500 mt-0.5">Auto-added as a deduction line on F&F compute.</p>
                  </label>
                )}
                <label className="block">
                  <span className="text-slate-700 font-medium">Notes</span>
                  <textarea value={updateForm.notes} onChange={e => setUpdateForm({ ...updateForm, notes: e.target.value })}
                    rows={2} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setUpdatingAsset(null)}>Cancel</Button>
                <Button size="sm" onClick={submitAssetUpdate} data-testid="update-submit-btn">Save</Button>
              </div>
            </div>
          </div>
        )}

        {/* Stage 73.20do — Per-asset Documents dialog */}
        {assetDocsFor && (
          <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setAssetDocsFor(null)}
            data-testid="asset-docs-modal"
          >
            <div
              className="bg-white rounded-lg p-5 max-w-2xl w-full max-h-[88vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Documents linked to <span className="text-blue-700">{assetDocsFor.asset_name}</span>
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {assetDocsFor.asset_type}{assetDocsFor.serial_number ? ` · Serial ${assetDocsFor.serial_number}` : ''}
                    {' · '}{assetDocs.length} attached
                  </p>
                </div>
                <button
                  onClick={() => setAssetDocsFor(null)}
                  className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Attached documents list */}
              <div className="border border-slate-200 rounded overflow-hidden mb-4">
                {assetDocsLoading ? (
                  <div className="p-4 text-center text-xs text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline-block" /> Loading…
                  </div>
                ) : assetDocs.length === 0 ? (
                  <p className="text-xs text-slate-500 italic p-3">
                    No documents attached yet. Use the form below to upload one.
                  </p>
                ) : (
                  <table className="w-full text-xs" data-testid="asset-docs-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">File</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Uploaded</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetDocs.map(d => (
                        <tr key={d.id} className="border-t border-slate-100" data-testid={`asset-doc-row-${d.id}`}>
                          <td className="p-2">
                            <span className="font-medium">{d.filename}</span>
                            {d.custom_label && <div className="text-[10px] text-slate-500">{d.custom_label}</div>}
                          </td>
                          <td className="p-2 text-slate-600">{d.category}</td>
                          <td className="p-2 text-slate-500 text-[11px]">{(d.uploaded_at || '').slice(0, 10)}</td>
                          <td className="p-2 text-right">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => downloadDocument(d)}
                              data-testid={`asset-doc-download-${d.id}`}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Inline uploader — pre-linked to the current asset */}
              <div className="border border-blue-200 bg-blue-50 rounded p-3">
                <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Attach a new document to this asset
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <label>
                    <span className="text-slate-700 font-medium">Document Type *</span>
                    <select
                      value={assetDocForm.category}
                      onChange={e => setAssetDocForm({ ...assetDocForm, category: e.target.value })}
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1 bg-white"
                      data-testid="asset-doc-category-select"
                    >
                      <option value="">— select —</option>
                      {docTypes.map(t => (
                        <option key={t.id} value={t.name}>{`${t.name}${t.is_asset_related ? ' · (asset-related)' : ''}`}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="text-slate-700 font-medium">Custom Label</span>
                    <input
                      value={assetDocForm.custom_label}
                      onChange={e => setAssetDocForm({ ...assetDocForm, custom_label: e.target.value })}
                      placeholder="Optional"
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                      data-testid="asset-doc-label-input"
                    />
                  </label>
                  <label className="md:col-span-2">
                    <span className="text-slate-700 font-medium">File *</span>
                    <input
                      type="file"
                      onChange={e => setAssetDocForm({ ...assetDocForm, file: e.target.files?.[0] || null })}
                      className="mt-1 w-full text-xs"
                      data-testid="asset-doc-file-input"
                    />
                  </label>
                  <label className="md:col-span-2">
                    <span className="text-slate-700 font-medium">Notes</span>
                    <textarea
                      value={assetDocForm.notes}
                      onChange={e => setAssetDocForm({ ...assetDocForm, notes: e.target.value })}
                      rows={2}
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                      data-testid="asset-doc-notes-input"
                    />
                  </label>
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={uploadAssetDoc}
                    disabled={assetDocUploading}
                    data-testid="asset-doc-upload-btn"
                    className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs"
                  >
                    {assetDocUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><Upload className="w-3 h-3 mr-1" /> Attach to this asset</>)}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end mt-3">
                <Button variant="outline" size="sm" onClick={() => setAssetDocsFor(null)}>Close</Button>
              </div>
            </div>
          </div>
        )}


        {/* === DOCUMENTS section === */}
        <Card className="p-4" data-testid="documents-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Documents ({documents.length})
            </h2>
            <Button
              onClick={() => setShowDocForm(s => !s)}
              size="sm"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
              data-testid="add-document-btn"
            >
              <Upload className="w-4 h-4 mr-1" /> Upload Document
            </Button>
          </div>

          {showDocForm && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3" data-testid="document-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <label>
                  <span className="text-slate-700 font-medium">Document Type *</span>
                  <select value={docForm.category} onChange={e => setDocForm({ ...docForm, category: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1" data-testid="doc-category-select">
                    <option value="">— select —</option>
                    {docTypes.map(t => (
                      <option key={t.id} value={t.name}>{`${t.name}${t.is_asset_related ? ' (asset-related)' : ''}`}</option>
                    ))}
                  </select>
                  {docTypes.length === 0 && (
                    <span className="text-[10px] text-amber-700">No active types — configure under Masters → Document Types.</span>
                  )}
                </label>
                <label>
                  <span className="text-slate-700 font-medium">Custom Label (optional)</span>
                  <input value={docForm.custom_label} onChange={e => setDocForm({ ...docForm, custom_label: e.target.value })}
                    placeholder="Used when category = Other" className="mt-1 w-full border border-slate-300 rounded px-2 py-1" />
                </label>
                {/* Stage 73.20di — Related asset dropdown appears when the
                    chosen document type is flagged is_asset_related. */}
                {(() => {
                  const chosenType = docTypes.find(t => t.name === docForm.category);
                  if (!chosenType?.is_asset_related) return null;
                  return (
                    <label className="md:col-span-2">
                      <span className="text-slate-700 font-medium">Related Asset *</span>
                      <select value={docForm.related_asset_id}
                        onChange={e => setDocForm({ ...docForm, related_asset_id: e.target.value })}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                        data-testid="doc-related-asset-select">
                        <option value="">— pick an issued asset —</option>
                        {assets.map(a => (
                          <option key={a.id} value={a.id}>{`${a.asset_type} · ${a.asset_name}${a.serial_number ? ` · ${a.serial_number}` : ''}`}</option>
                        ))}
                      </select>
                    </label>
                  );
                })()}
                <label>
                  <span className="text-slate-700 font-medium">Expiry Date (optional)</span>
                  <input type="date" value={docForm.expiry_date}
                    onChange={e => setDocForm({ ...docForm, expiry_date: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                    data-testid="doc-expiry-date" />
                </label>
                <label className="md:col-span-2">
                  <span className="text-slate-700 font-medium">File * (PDF / image / docx, max 10MB)</span>
                  <input type="file" onChange={e => setDocForm({ ...docForm, file: e.target.files?.[0] || null })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1 bg-white" data-testid="doc-file-input" />
                </label>
                <label className="md:col-span-2">
                  <span className="text-slate-700 font-medium">Notes</span>
                  <textarea value={docForm.notes} onChange={e => setDocForm({ ...docForm, notes: e.target.value })}
                    rows={2} className="mt-1 w-full border border-slate-300 rounded px-2 py-1" />
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setShowDocForm(false)}>Cancel</Button>
                <Button size="sm" onClick={uploadDocument} disabled={docUploading || !docForm.file} data-testid="doc-upload-btn">
                  {docUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Upload'}
                </Button>
              </div>
            </div>
          )}

          {documents.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No documents uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="documents-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Category</th>
                    <th className="text-left p-2">Filename</th>
                    <th className="text-left p-2">Size</th>
                    <th className="text-left p-2">Uploaded</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(d => (
                    <tr key={d.id} className="border-t border-slate-100" data-testid={`doc-row-${d.id}`}>
                      <td className="p-2">
                        <div>{d.category}</div>
                        {d.custom_label && <div className="text-[10px] text-slate-500">{d.custom_label}</div>}
                      </td>
                      <td className="p-2 font-medium">{d.filename}</td>
                      <td className="p-2 text-slate-600">
                        {(d.size_bytes / 1024).toFixed(1)} KB
                      </td>
                      <td className="p-2 text-slate-600">{(d.uploaded_at || '').slice(0, 10)}</td>
                      <td className="p-2 text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => previewDocument(d)} title="Preview Document" data-testid={`doc-preview-btn-${d.id}`}>
                          <Eye className="w-3 h-3 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadDocument(d)} title="Download Document" data-testid={`doc-download-btn-${d.id}`}>
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteDocument(d.id)} className="text-rose-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
}
